import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-ground-alt flex items-center justify-center p-6">
          <div className="bg-white border border-ground-deep shadow-xl p-8 md:p-12 text-center max-w-md w-full relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-bl-full -mr-8 -mt-8" />
            
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-6 relative z-10" />
            
            <h2 className="text-2xl font-serif text-ink mb-4 relative z-10">
              Something went wrong
            </h2>
            
            <p className="text-ink/70 mb-8 relative z-10">
              We encountered an unexpected error while trying to load this page. 
              {this.state.error?.message?.includes('fetch') || this.state.error?.message?.includes('load') ? 
                ' This might be due to a temporary network issue or a recent update.' : 
                ' Our team has been notified.'}
            </p>

            <button
              onClick={() => {
                this.setState({ hasError: false, error: undefined });
                window.location.reload();
              }}
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium text-ink bg-rule-3 hover:bg-rule-3/90 transition-colors w-full relative z-10 gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
